"use client";

import { useEffect, useState, useSyncExternalStore } from "react";
import Link from "next/link";
import { useLocale, useTranslations } from "use-intl";
import { navLabel } from "@/lib/i18n/nav-labels";
import { PerksmithCounterLockup, PerksmithMark, PerksmithRewardsLockup } from "@/components/brand/perksmith-mark";
import { CounterButton, Icon } from "@/components/counter";
import { packLockup, packNav, PACK_BY_ID, type PackId } from "@/lib/packs";
import { cn } from "@/lib/utils";
import { rememberActivePack } from "@/lib/hooks/useActivePack";
import { usePosSession, staffInitials, staffRoleLabel } from "@/lib/hooks/usePosSession";
import { signOut } from "@/lib/api/session";
import { LanguageQuickToggle } from "@/components/shell/LanguageQuickToggle";
import { BACK_OFFICE_RAIL, canSeeBackOffice } from "@/lib/packs";
import { StaffPinSwitchDialog } from "@/components/shell/StaffPinSwitchDialog";
import { SetOwnPinDialog } from "@/components/shell/SetOwnPinDialog";

/** One rail destination. `icon` is an Icon name; `href` makes it navigable. */
export type RailItem = {
  id: string;
  label: string;
  icon?: string;
  href?: string;
};

/**
 * Default nav (restaurant/counter). Packs pass their own via `nav` — POS-WEB-KIT keeps the
 * rail itself pack-agnostic so each pack screen owns only its own folder.
 */
/**
 * Last-resort nav, used only when a screen names neither `nav` nor `pack`. Every item carries an
 * href: an unlinked fallback item is a button that looks navigable and does nothing, which is
 * indistinguishable from a broken rail.
 */
export const DEFAULT_NAV: RailItem[] = [
  { id: "floor", label: "Floor map", href: "/restaurant" },
  { id: "orders", label: "Orders", href: "/" },
  { id: "menu", label: "Menu", href: "/catalog" },
  { id: "customers", label: "Customers", href: "/customers" },
  { id: "shifts", label: "Shifts", href: "/shifts" },
  { id: "reports", label: "Reports", href: "/reports" },
  { id: "settings", label: "Settings", href: "/settings" },
];

/** Nothing external fires when the URL changes on its own — see `useActivePack`/`useVocab`. */
const noSubscribe = () => () => {};
const noAsPack = (): PackId | undefined => undefined;

function readAsPackFromUrl(): PackId | undefined {
  const as = new URLSearchParams(window.location.search).get("as");
  return as && as in PACK_BY_ID ? (as as PackId) : undefined;
}

/** Whether hydration has committed — see `useCounterClock` in `lib/time/wallClock.ts`. */
const mounted = () => true;
const notMounted = () => false;

/** 72px indigo icon rail — the shared POS shell nav (Ledger Ink). */
export function PosRail({
  active = "orders",
  nav,
  pack,
}: {
  active?: string;
  nav?: RailItem[];
  /** Current business type — resolves this pack's navigation. */
  pack?: PackId;
}) {
  /**
   * Who is signed in comes from the SESSION, never from the screen.
   *
   * Every screen used to pass its own literal — "Priya the Cashier", "Kavya the Owner" — so a real
   * business would have seen a stranger's name on their own counter, and it changed as you walked
   * between screens. There is exactly one person at this till and the server knows who.
   */
  const session = usePosSession();
  const t = useTranslations("rail");
  const locale = useLocale();
  // The back-office group is decided by the role /pos/context reports, so this is
  // server-declared truth rather than a client-invented matrix. The back-office
  // endpoints enforce their own permissions regardless: hiding the group spares a
  // cashier a row of links that would 403, it is not the security boundary.
  const showBackOffice = canSeeBackOffice(session.staff?.role);
  const staff = {
    name: session.staff?.name ?? "",
    // A role the catalog knows is said in the till's language; a new one degrades to its readable
    // English label rather than to a blank.
    role:
      session.staff?.role && t.has(`roles.${session.staff.role}` as "roles.CASHIER")
        ? t(`roles.${session.staff.role}` as "roles.CASHIER")
        : staffRoleLabel(session.staff?.role),
    initial: staffInitials(session.staff?.name),
  };
  // Icon-only is unreadable until you have learned the set, so the rail opens to labels and
  // remembers the choice. Collapsed stays available for a small counter screen.
  /**
   * `?as=<packId>` lets an expansion vertical borrow its host's screens.
   *
   * Every host screen passes its own literal pack, so a coaching centre, a spa or an optical
   * store rendered the HOST's rail — their own nav existed in the registry and was never once
   * shown. Resolving it here means no screen has to remember, which is the same reason the
   * pack-nav fallback lives here (see `items` below).
   */
  /**
   * `?as=` on the URL — not reactive in the sense of firing external events, but re-read on every
   * render already (React calls `getSnapshot` each time), which is what let this used to depend on
   * `[pack]` and re-run for. See "You Might Not Need an Effect" and `useVocab`'s identical read.
   *
   * Deliberately no fallback to the remembered vertical here. The URL is the single source of
   * truth for which business you are in: every rail link now carries `?as=`, so in-app
   * navigation keeps it, while a bare /salon means salon. Falling back to storage made a plain
   * host URL ambiguous — a salon that had once been viewed as a spa kept showing the spa.
   */
  const asPack = useSyncExternalStore(noSubscribe, readAsPackFromUrl, noAsPack);
  const activePack = asPack ?? pack;

  /**
   * Rail links carry the vertical with them. Without it the URL cannot describe which business
   * you are in, so a reload, a shared link or the browser's back button all land in the host.
   */
  const expansion = activePack ? PACK_BY_ID[activePack]?.reuses : undefined;
  const withPack = (href: string) =>
    expansion && !href.includes("?") ? `${href}?as=${activePack}` : href;

  const [expanded, setExpanded] = useState(true);
  const [switchOpen, setSwitchOpen] = useState(false);
  const [setPinOpen, setSetPinOpen] = useState(false);

  /**
   * A rail told which vertical it is in resolves that vertical's nav. It used to fall back to
   * DEFAULT_NAV whenever `nav` was omitted, so the checkout screen — which mounts the rail
   * directly rather than through PosShell — rendered a placeholder list whose items carry no
   * href. The result was a sidebar that changed shape on /, with nothing clickable on it.
   */
  const items = (asPack ? packNav(asPack) : nav) ?? packNav(activePack) ?? DEFAULT_NAV;

  // Record the vertical so the shared back-office screens keep this rail (see useActivePack).
  useEffect(() => {
    if (activePack) rememberActivePack(activePack);
  }, [activePack]);
  /**
   * Labels on a counter screen, icons on a tablet — unless the operator has said otherwise.
   *
   * <p>The rail defaulted to its 212px labelled form at every width. On a 1024px iPad that is a
   * fifth of the screen spent on navigation, and with the 384px ledger beside it the workspace was
   * squeezed to about 570px: "Register walk-in" was clipped mid-word and a patient's name rendered
   * as "Aa…". Nothing overflowed, which is why it never looked broken enough to notice.
   *
   * <p>A stored preference still wins at every width. This only changes what the operator gets
   * before they have expressed one, and the toggle is where it always was.
   */
  /**
   * `expanded` stays a plain, freely-settable `useState` — `toggle` below writes to it directly —
   * so the browser-only seed value cannot be read via `useSyncExternalStore` alone the way
   * `asPack` is. Instead, `hasMounted` (server snapshot `false`, exactly `useCounterClock`'s
   * pattern) gates a render-phase adjustment: the very first client render after hydration reads
   * localStorage/`matchMedia` — real browser state absent from the server-rendered markup — and
   * seeds `expanded` right here rather than with a setState call at the top of an Effect. See "You
   * Might Not Need an Effect": adjusting state when a prop changes.
   */
  const hasMounted = useSyncExternalStore(noSubscribe, mounted, notMounted);
  const [seededExpanded, setSeededExpanded] = useState(false);
  if (hasMounted && !seededExpanded) {
    setSeededExpanded(true);
    const saved = window.localStorage.getItem("pos.rail.expanded");
    // Matches the `lg` breakpoint the workspace is laid out against.
    setExpanded(saved !== null ? saved === "1" : window.matchMedia("(min-width: 1280px)").matches);
  }
  const toggle = () => {
    setExpanded((v) => {
      window.localStorage.setItem("pos.rail.expanded", v ? "0" : "1");
      return !v;
    });
  };

  return (
    <nav
      data-testid="pos-primary-nav"
      className={cn(
        "flex h-full shrink-0 flex-col gap-1 bg-bg-rail py-4 text-text-on-rail transition-[width] duration-150",
        expanded ? "w-[212px] items-stretch px-2.5" : "w-[72px] items-center",
      )}
      aria-label={t("primary")}
    >
      <div className={cn("mb-2 flex items-center", expanded ? "justify-between px-1" : "flex-col gap-2")}>
        {/* Home is this business's own counter. It used to open the screen-gallery review
            harness, which is a build tool and not somewhere a shop should ever land. */}
        {/* The lockup, never the typed name: the wordmark IS the mark (register B-06), so the
            rail shows Perksmith Counter set in its own letters with the honey tick, and the
            symbol alone when the rail is collapsed. "POS" is the category, not the product. */}
        <Link href="/" title={t("home")} className="flex items-center gap-2">
          {/* The lockup follows the surface, and the pack row says which (DESIGN.md §4, Q-16): the
              Reward Desk is a Perksmith Rewards feature and wears the Rewards cut in this same app. */}
          {expanded ? (
            packLockup(activePack) === "rewards" ? (
              <PerksmithRewardsLockup size={16} mono className="text-text-on-fill" />
            ) : (
              <PerksmithCounterLockup size={16} mono className="text-text-on-fill" />
            )
          ) : (
            <PerksmithMark size={24} />
          )}
          <span className="sr-only">{t("home")}</span>
        </Link>
        <CounterButton
          tone="on-rail"
          size="icon"
          onClick={toggle}
          aria-expanded={expanded}
          aria-label={expanded ? t("collapse") : t("expand")}
          title={expanded ? t("collapse") : t("expand")}
          className="shrink-0 rounded-tile"
        >
          <svg viewBox="0 0 20 20" className="h-[18px] w-[18px]" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
            <rect x="2.5" y="3.5" width="15" height="13" rx="2" />
            <line x1="8" y1="3.5" x2="8" y2="16.5" />
            {expanded ? <polyline points="13,7.5 10.8,10 13,12.5" /> : <polyline points="11,7.5 13.2,10 11,12.5" />}
          </svg>
        </CounterButton>
      </div>
      {expanded && (
        <p className="mb-1 px-2 text-sm font-bold uppercase tracking-[0.12em] text-text-on-rail/55">
          {t("menuHeading")}
        </p>
      )}
      <ul className={cn("flex flex-1 flex-col gap-0.5", expanded ? "items-stretch" : "items-center gap-1")}>
        {items.map((item) => {
          const isActive = item.id === active;
          const className = cn(
            "flex items-center rounded-pill transition-colors",
            expanded ? "h-11 w-full gap-2.5 px-2.5" : "h-12 w-12 justify-center",
            isActive
              ? "bg-stroke-on-rail text-text-on-fill"
              : "text-text-on-rail hover:bg-stroke-on-rail/60 hover:text-text-on-fill",
          );
          const inner = (
            <>
              <Icon name={item.icon ?? item.id} />
              {expanded ? (
                <span className="truncate text-sm font-semibold">{navLabel(item.label, locale)}</span>
              ) : (
                <span className="sr-only">{navLabel(item.label, locale)}</span>
              )}
              {expanded && isActive && (
                <span className="ml-auto h-1.5 w-1.5 shrink-0 rounded-full bg-bg-base/70" />
              )}
            </>
          );
          return (
            <li key={item.id} className={expanded ? "w-full" : undefined}>
              {item.href ? (
                <Link href={withPack(item.href)} aria-current={isActive ? "page" : undefined} title={navLabel(item.label, locale)} className={className}>
                  {inner}
                </Link>
              ) : (
                <CounterButton
                  tone="on-rail"
                  aria-current={isActive ? "page" : undefined}
                  title={navLabel(item.label, locale)}
                  className={className}
                >
                  {inner}
                </CounterButton>
              )}
            </li>
          );
        })}
      </ul>
      {showBackOffice && (
        <>
          {expanded && (
            <p className="mb-1 mt-2 px-2 text-sm font-bold uppercase tracking-[0.12em] text-text-on-rail/55">
              {t("backOfficeHeading")}
            </p>
          )}
          <ul className={cn("flex flex-col gap-0.5", expanded ? "items-stretch" : "items-center gap-1")}>
            {BACK_OFFICE_RAIL.map((item) => {
              const isActive = item.id === active;
              const className = cn(
                "flex items-center rounded-pill transition-colors",
                expanded ? "h-11 w-full gap-2.5 px-2.5" : "h-12 w-12 justify-center",
                isActive
                  ? "bg-stroke-on-rail text-text-on-fill"
                  : "text-text-on-rail hover:bg-stroke-on-rail/60 hover:text-text-on-fill",
              );
              return (
                <li key={item.id} className={expanded ? "w-full" : undefined}>
                  <Link
                    href={item.href ?? "#"}
                    aria-current={isActive ? "page" : undefined}
                    title={navLabel(item.label, locale)}
                    className={className}
                  >
                    <Icon name={item.icon ?? item.id} />
                    {expanded ? (
                      <span className="truncate text-sm font-semibold">{navLabel(item.label, locale)}</span>
                    ) : (
                      <span className="sr-only">{navLabel(item.label, locale)}</span>
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>
        </>
      )}
      <div
        data-testid="pos-staff"
        className={cn("mt-2 flex items-center", expanded ? "gap-2.5 rounded-pill bg-stroke-on-rail/50 px-2 py-1.5" : "flex-col gap-1.5")}
        title={staff.name ? `${staff.name} · ${staff.role}` : undefined}
      >
        <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-stroke-on-rail font-text text-sm font-bold text-text-on-fill">
          {staff.initial}
        </div>
        {expanded && (
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-text-on-fill">
              {staff.name || (session.loading ? "\u00a0" : t("signedIn"))}
            </p>
            <p className="truncate text-sm text-text-on-rail/70">{staff.role}</p>
          </div>
        )}
        {/* The language, one press from the person's own row (saved to their staff profile). */}
        <LanguageQuickToggle variant="rail" />
        {/* A same-shift handoff: a colleague proves who they are with their own PIN, and
            this till's session becomes theirs. Beside sign-out, not behind a menu — a shared till
            hands off many times a shift, far more often than anyone actually signs out of it. */}
        <CounterButton
          tone="on-rail"
          size="icon"
          data-testid="pos-switch-staff"
          onClick={() => setSwitchOpen(true)}
          title={t("switchStaff")}
          aria-label={t("switchStaff")}
          className="shrink-0 rounded-tile"
        >
          <svg viewBox="0 0 20 20" className="h-[17px] w-[17px]" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
            <path d="M3.5 7h9M12.5 7l-2.3-2.3M12.5 7l-2.3 2.3" />
            <path d="M16.5 13h-9M7.5 13l2.3-2.3M7.5 13l2.3 2.3" />
          </svg>
        </CounterButton>
        {/* A cashier's first pos-webapp sign-in has no PIN yet; this is where they set one so a
            colleague can hand them the till later without needing their password. Sets or
            replaces it either way, so it stays reachable even to change a PIN already set. */}
        <CounterButton
          tone="on-rail"
          size="icon"
          data-testid="pos-set-pin"
          onClick={() => setSetPinOpen(true)}
          title={t("setMyPin")}
          aria-label={t("setMyPin")}
          className="shrink-0 rounded-tile"
        >
          <svg viewBox="0 0 20 20" className="h-[17px] w-[17px]" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
            <circle cx="10" cy="7" r="3" />
            <path d="M6 16c0-2.2 1.8-4 4-4s4 1.8 4 4" />
          </svg>
        </CounterButton>
        {/* A counter machine is shared and often unattended. Ending your own shift has to be one
            reachable control, not a browser cookie somebody has to know how to clear. */}
        <CounterButton
          tone="on-rail"
          size="icon"
          onClick={() => void signOut()}
          title={t("signOut")}
          aria-label={staff.name ? t("signOutNamed", { name: staff.name }) : t("signOut")}
          className="shrink-0 rounded-tile"
        >
          <svg viewBox="0 0 20 20" className="h-[17px] w-[17px]" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
            <path d="M12.5 6.5V4.8a1.3 1.3 0 0 0-1.3-1.3H5.3A1.3 1.3 0 0 0 4 4.8v10.4a1.3 1.3 0 0 0 1.3 1.3h5.9a1.3 1.3 0 0 0 1.3-1.3V13.5" />
            <path d="M8.5 10h8" />
            <polyline points="14,7.5 16.5,10 14,12.5" />
          </svg>
        </CounterButton>
        {!expanded && (
          <span className="sr-only">
            {staff.name}, {staff.role}
          </span>
        )}
      </div>
      <StaffPinSwitchDialog open={switchOpen} ownStaffId={session.staff?.id} onClose={() => setSwitchOpen(false)} />
      <SetOwnPinDialog open={setPinOpen} onClose={() => setSetPinOpen(false)} />
    </nav>
  );
}
